/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useMutation,
  DocumentNode,
  TypedDocumentNode,
  MutationHookOptions,
  MutationTuple,
  FetchResult,
} from '@apollo/client'
import { useCallback } from 'react'

import { useStateValue } from 'contexts'
import { defaultMutationErrorHandler } from 'apollo/utils'

type UsePrivilegedMutationOptions<TData, TVariables> = MutationHookOptions<
  TData,
  TVariables
>

/**
 * A hook that wraps Apollo's useMutation to handle privileged operations requiring re-authentication.
 *
 * This hook is designed for mutations that may require additional authentication (like password check and MFA verification)
 * when the server responds with privilegedInfo.reauthNeeded. It will:
 * 1. Execute the mutation
 * 2. Check for privilegedInfo.reauthNeeded in the response
 * 3. If reauth is needed:
 *    - Show the reauth modal
 *    - Wait for reauth completion
 *    - Retry the mutation in case of error
 */
const usePrivilegedMutation = <TData = any, TVariables = any>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: UsePrivilegedMutationOptions<TData, TVariables> = {}
): MutationTuple<TData, TVariables> => {
  const [mutateFn, mutationResult] = useMutation<TData, TVariables>(mutation, {
    onError: defaultMutationErrorHandler,
    ...options,
  })

  const {
    actions: { uiActions },
  } = useStateValue()

  const showReauthModal = useCallback(
    (): Promise<void> =>
      new Promise((resolve, reject) => {
        uiActions.setReauthModalOptions({
          isReauthModalVisible: true,
          onComplete: resolve,
          onError: reject,
        })
      }),
    [uiActions]
  )

  const wrappedMutate: typeof mutateFn = async args => {
    const execute = async (): Promise<FetchResult<TData>> => {
      const result = await mutateFn(args)

      // GraphQL mutation responses are objects with mutation names as keys
      // Object.values(data)[0] gets the first (and usually only) mutation result
      const resultData: any = result.data && Object.values(result.data)[0]

      if (resultData?.privilegedInfo?.reauthNeeded) {
        await showReauthModal()
        // retry after reauth
        return execute()
      }

      return result
    }

    return execute()
  }

  return [wrappedMutate, mutationResult]
}

export default usePrivilegedMutation
